import { isArray } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  UseGetCustomisationResponse,
  UseGetProductResponse,
} from "types/queries";
import { getUpsellPriceFromProduct } from "utils/extras-utils";

const addonsKeys = [
  "cover_style",
  "dedication_photo_upload",
  "format",
  "giftwrap",
] as const;

type Component = {
  id: string;
};

export type SubtotalData = {
  cover_style: number;
  currency?: string;
  dedication_photo_upload: number;
  format: number;
  formattedSubtotal?: string;
  giftwrap: number;
  savingSubtotal: number;
  subtotal: number;
  edition: number;
};

export type NumericalSubtotalData = Omit<
  SubtotalData,
  "formattedSubtotal" | "currency"
>;

export type SubtotalKey = (typeof addonsKeys)[number];

const isNumericalSubtotalData = (key: string) => {
  return (
    key === "cover_style" ||
    key === "dedication_photo_upload" ||
    key === "format" ||
    key === "giftwrap" ||
    key === "edition"
  );
};

const calculateSubtotal = (subtotalData: SubtotalData) => {
  const subTotalItems = Object.keys(subtotalData);

  if (subTotalItems.length === 0) {
    return null;
  }

  const subTotal = subTotalItems.reduce((acc, key) => {
    if (!isNumericalSubtotalData(key)) {
      return acc;
    }

    const value = subtotalData[key as keyof NumericalSubtotalData];

    return acc + value;
  }, 0);

  return {
    ...subtotalData,
    subtotal: Number(subTotal.toFixed(2)),
    savingSubtotal: subtotalData.savingSubtotal || 0,
  };
};

const updateSubtotalData = (
  subtotalData: SubtotalData,
  update: { [key: string]: number }
): SubtotalData => {
  const updatedData = { ...subtotalData, ...update };
  const calculated = calculateSubtotal(updatedData);

  if (calculated) {
    return {
      ...updatedData,
      subtotal: calculated.subtotal,
      savingSubtotal: calculated.savingSubtotal,
    };
  }

  return updatedData;
};

export const initialData: SubtotalData = {
  cover_style: 0,
  dedication_photo_upload: 0,
  format: 0,
  giftwrap: 0,
  subtotal: 0,
  savingSubtotal: 0,
  edition: 0,
};

export const useSubtotal = (bookSlug?: string) => {
  const [subtotalData, setSubtotalData] = useState<SubtotalData>({
    ...initialData,
  });

  useEffect(() => {
    // Initialize subtotalData from localStorage or any other source
    const storedData = localStorage.getItem(`${bookSlug}:subtotal`);

    if (!storedData && bookSlug) {
      setSubtotalData(initialData);
      localStorage.setItem(`${bookSlug}:subtotal`, JSON.stringify(initialData));
    }
  }, [bookSlug]);

  const getSubtotalPropertyPrice = useCallback(
    (propertyName: PropertyKey) => {
      const storedData = localStorage.getItem(`${bookSlug}:subtotal`);

      if (!storedData) {
        console.warn("No subtotal data found in localStorage.");
        return 0;
      }

      const subtotalData: SubtotalData = JSON.parse(storedData);
      return subtotalData[propertyName as keyof NumericalSubtotalData] || 0;
    },
    [bookSlug]
  );

  const updateSubtotal = (update: { [key: string]: number }) => {
    const storedData = localStorage.getItem(`${bookSlug}:subtotal`);

    if (!storedData) {
      console.warn("No subtotal data found in localStorage.");
      return;
    }

    const updatedSubtotalData = updateSubtotalData(
      JSON.parse(storedData),
      update
    );

    setSubtotalData({
      ...updatedSubtotalData,
    });

    localStorage.setItem(
      `${bookSlug}:subtotal`,
      JSON.stringify(updatedSubtotalData)
    );
  };

  const getSubtotalWithExclusion = useCallback(
    (exclusion?: string) => {
      const storedData = localStorage.getItem(`${bookSlug}:subtotal`);

      if (!storedData) {
        console.warn("No subtotal data found in localStorage.");
        return null;
      }

      const subtotalData: SubtotalData = JSON.parse(storedData);
      delete subtotalData[exclusion as keyof SubtotalData];

      const subtotal = calculateSubtotal(subtotalData);
      return subtotal?.subtotal;
    },
    [bookSlug]
  );

  const initiateSubtotalForEdit = useCallback(
    (
      customisation: UseGetCustomisationResponse & {
        components?: Component[];
      },
      product: UseGetProductResponse
    ) => {
      // const storedData = localStorage.getItem(`${bookSlug}:subtotal`);
      const { components } = customisation;
      const initialSubtotalData = initialData;

      if (isArray(components)) {
        components.forEach((component) => {
          const [key, value] = [
            component.id.split(":")[0],
            component.id.split(":")[1],
          ];

          initialSubtotalData[key as SubtotalKey] =
            getUpsellPriceFromProduct(key, value, product) || 0;
        });

        localStorage.setItem(
          `${bookSlug}:subtotal`,
          JSON.stringify(calculateSubtotal(initialSubtotalData))
        );
      }
    },
    [bookSlug]
  );

  return {
    subtotal: subtotalData,
    updateSubtotal,
    setSubtotalData,
    getSubtotalPropertyPrice,
    getSubtotalWithExclusion,
    initiateSubtotalForEdit,
  };
};
